import React from 'react';
import { Box, Typography, Card, CircularProgress, Button, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmbeddedSignup from './EmbeddedSignup';

function WhatsAppSettings({ statusData, loading, clinicId, onConnectSuccess, onDisconnect }) {
  const theme = useTheme();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  const isConnected = statusData?.status === 'CONNECTED';

  return (
    <Box>
      <Typography variant="h3" fontWeight="bold" mb={1}>
        WhatsApp Integration
      </Typography>
      <Typography variant="body1" color="textSecondary" mb={4}>
        Connect your WhatsApp Business account to enable automated messaging and templates.
      </Typography>

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
        {/* Main Action Card */}
        <Card sx={{ p: 4, flex: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[1] }}>
          {!isConnected ? (
            <Box textAlign="center" py={4}>
              <Box mb={3}>
                <EmbeddedSignup clinicId={clinicId} onConnectSuccess={onConnectSuccess} />
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                Link your official WhatsApp Business API account for automated messaging. Set up your Meta Business Account to submit your templates for data and server settings.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" color="success.main" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), px: 1, py: 0.5, borderRadius: 1, display: 'inline-block', mb: 0.5 }}>
                    Connected
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {statusData?.displayPhoneNumber || 'Connected Number'}
                  </Typography>
                </Box>
                <Box ml="auto">
                  <Button variant="outlined" color="error" onClick={onDisconnect}>
                    Disconnect
                  </Button>
                </Box>
              </Box>

              <Box display="flex" gap={4} mt={2}>
                <Box>
                  <Typography variant="caption" color="textSecondary">Business Name</Typography>
                  <Typography variant="body1" fontWeight="500">{statusData?.businessName || 'MyCliniq User'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Quality Rating</Typography>
                  <Typography variant="body1" fontWeight="500" color="success.main">{statusData?.qualityRating || 'GREEN'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">Messaging Limit</Typography>
                  <Typography variant="body1" fontWeight="500">{statusData?.messagingLimit || 'Tier 1K'}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Card>

        {/* Status Card */}
        <Card sx={{ p: 3, flex: 1, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[1], height: 'fit-content' }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
            {!isConnected ? <InfoOutlinedIcon color="disabled" /> : <CheckCircleIcon color="success" />}
            <Typography variant="h6">
              Status: {isConnected ? 'Connected' : 'Not Connected'}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary">
            {isConnected 
              ? 'WhatsApp messaging is active. You can now send automated messages and broadcasts.' 
              : 'WhatsApp messaging not active. Connect your account to get started.'}
          </Typography>
        </Card>
      </Box>


    </Box>
  );
}

export default WhatsAppSettings;
